import React, { useState, memo, useEffect } from 'react'
import styled from 'styled-components'

import { ReactComponent as Ellipsis } from '../../../assets/ellipsis.svg'
import { ReactComponent as LockIcon } from '../../../assets/lock.svg'

import { LazyLoadImage } from 'react-lazy-load-image-component'
import Image from 'next/image'

const Card = styled.div`
  background-color: #ffffff;

  position: relative;

  border-radius: 10px;
  overflow: hidden;
  user-select: none;
  cursor: pointer;

  width: 100%;
  height: 100%;

  @media only screen and (max-width: 768px) {
    border-radius: 10px;
  }
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.3);

  transition: visibility 0.2s, opacity 0.2s linear;

  display: none;
  justify-content: space-evenly;
  align-items: center;

  @media only screen and (min-width: 768px) {
    display: flex;
  }
`

const MenuOpenButton = styled.button`
  position: absolute;
  top: 0;
  right: 12px;
  background-color: transparent;
  outline: none;
  border: none;
  height: 40px;
  cursor: pointer;
  padding: 0;
  z-index: 5;
`

const Menu = styled.div`
  position: absolute;
  top: 36px;
  right: 12px;
  background-color: white;
  border-radius: 5px;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 106px;
  z-index: 6;
`

const MenuButton = styled.button`
  background-color: transparent;
  outline: none;
  border: none;
  height: 40px;
  cursor: pointer;
  margin: 0 3px;
  font-weight: bold;
  font-size: 14px;
  color: #8b8b8b;

  &:hover {
    background-color: #f5f5f5;
  }
`

const Lock = styled(LockIcon)``

const BaseDesignCard = ({
  refFn,
  moved,
  template,
  children,
  menuItems,
  lock,
  onUse,
  data,
  ...props
}) => {
  const [showOverlay, setShowOverlay] = useState('hidden')
  const [showMenu, setShowMenu] = useState(false)

  const [alt, setAlt] = useState('DecorMatters Design')
  const [src, setSrc] = useState('')
  const [srcSet, setSrcSet] = useState()

  useEffect(() => {
    if (!template) return

    var test = template.split('.')[0]
    if (test !== 'https://cdn') {
      setSrc(template)
      return
    }

    const url = template.split('/')
    const filename = url.pop()
    const cdn = url.join('/') + '/'

    const breakdown = filename.split('.')
    const url244 = cdn + breakdown[0] + '-244w.webp'
    //+ breakdown[1]
    const url314 = cdn + breakdown[0] + '-314w.webp'
    //+ breakdown[1]

    setAlt('Design Template')
    setSrc(url314)

    const theSet = url244 + ' 244w, ' + url314 + ' 314w'
    setSrcSet(theSet)
  }, [template])

  useEffect(() => {
    if (!data) return
    if (data.collection) {
      const collection = data.collection
      setAlt(collection.alt)

      const url244 = collection.cdn + collection.key + '-244w.webp'
      const url314 = collection.cdn + collection.key + '-314w.webp'
      setSrc(url314)

      const theSet = url244 + ' 244w, ' + url314 + ' 314w'
      setSrcSet(theSet)
    } else {
      if (data.cfThumbImageUrl) {
        setSrc(data.cfThumbImageUrl)
      } else if (data.cfBgImageUrl) {
        setSrc(data.cfBgImageUrl)
      } else if (data.cfImageUrl) {
        setSrc(data.cfImageUrl)
      }
    }
  }, [data])
  const handleCardClick = e => {
    if (lock) return
    if (moved !== undefined && moved !== 0) return

    if (onUse) {
      onUse(e)
    }
  }

  const handleMouseOver = e => {
    e.preventDefault()
    if (e.target.id === 'overlay') setShowMenu(false)
    setShowOverlay('visible')
  }

  const handleMouseOut = e => {
    e.preventDefault()
    setShowOverlay('hidden')
    if (e.target.id === 'overlay') setShowMenu(false)
  }

  const handleShowMenu = e => {
    e.stopPropagation()
    setShowMenu(s => !s)
  }

  return (
    <Card
      ref={refFn}
      id="overlay"
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onClick={handleCardClick}
    >
      {src && (
        <Image
          src={src}
          width="300px"
          height="300px"
          layout="responsive"
          sizes="(max-width: 786px) 140px, (min-width: 1100px) 200px, (min-width: 1300px) 300px"
          quality={80}
          alt={`${data.title} template`}
          priority
        />
      )}
      {!lock && (
        <Overlay
          style={{
            visibility: showOverlay,
            opacity: showOverlay === 'visible' ? 1 : 0
          }}
          onClick={e => setShowMenu(false)}
        >
          {menuItems && (
            <MenuOpenButton
              onClick={handleShowMenu}
              aria-label="Show Design Menu"
            >
              <Ellipsis />
            </MenuOpenButton>
          )}
          {children}
          {showMenu && menuItems && (
            <Menu>
              {menuItems.map((d, i) => (
                <MenuButton
                  key={i}
                  style={{
                    color: d.highlight === true && '#ff5e6d',
                    borderBottom: '1px solid #E5E5E5'
                  }}
                  onClick={d.onClick}
                  aria-label={d.title + ' option'}
                >
                  {d.title}
                </MenuButton>
              ))}
            </Menu>
          )}
        </Overlay>
      )}
      {lock && (
        <Overlay>
          <Lock />
        </Overlay>
      )}
    </Card>
  )
}

export default memo(BaseDesignCard)
